import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import AssociateDocumentDialog from './AssociateDocumentDialog';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import {
  pageSizeChangeHandler,
  useNotification,
  getSnackBar,
  showBackdropLoader,
} from 'utils/commonUtils';
import { booleanOptions, intialPageSize, companyOptions } from 'constants';
import ActiveCompanyAssociationsDialog from './ActiveCompanyAssociationsDialog';
import {
  getEmploymentDocs,
  getDocumentTypes,
  postDownloadEmploymentDocument,
  deleteEmploymentDocument,
} from 'layouts/documentManagement/services/documentManagementApi';
import {
  GridWrapper,
  HeadingMuiTypography,
  GridBaselineWrapper,
} from 'components/Ui/styled';
import {
  employeeDocumentsColumns,
  getEmployeeDocumentsListData,
} from './EmployeeDocumentsDataGrid';
import {
  setCompany,
  setDocumentType,
  setIncludeExpired,
  setIsActiveCompanyAssociation,
} from 'layouts/documentManagement/store/employeeDocumentsFilterSlice/employeeDocumentsFilterSlice';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import ConfirmationDialog from 'components/Common/ConfirmationDialog/ConfirmationDialog';

const EmployeeDocumentsTab = () => {
  const dispatch = useDispatch();

  const { handleErrorResponse, NotificationPopup } = useNotification();

  const [employeeDocuments, setEmployeeDocuments] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
    page: 1,
  });
  const [activeCompanyAssociationsDialog, setActiveCompanyAssociationsDialog] =
    useState(false);
  const [associateDocumentDialog, setAssociateDocumentDialog] = useState(false);
  const [isLoadingBackDrop, setIsLoadingBackDrop] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [deleteDocument, setDeleteDocument] = useState(null);
  const [loadingButton, setLoadingButton] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);

  const employee = useSelector((state) => state.employee?.employee);

  const employeeDocumentFilters = useSelector(
    (state) => state.employeeDocumentFilters
  );

  const employeeDetails = useSelector((state) => state.employeeDetails);

  const { activeCompanyAssociations } = employeeDetails || {};

  const { company, documentType, includeExpired, isActiveCompanyAssociation } =
    employeeDocumentFilters;

  const pageSizeChange = pageSizeChangeHandler(setEmployeeDocuments);

  const defaultValue = {
    company: company,
    documentType: documentType,
    includeExpired: includeExpired,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { control, register, setValue } = useFunction;

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  const fetchDocumentTypes = async () => {
    try {
      const documentTypes = await getDocumentTypes();
      setDocumentTypes(documentTypes?.documentTypes);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    fetchEmployeeDocuments();
  }, [company, includeExpired, documentType]);

  const fetchEmployeeDocuments = async () => {
    setEmployeeDocuments({ ...employeeDocuments, isLoading: true });
    try {
      const employeeDocumentsData = await getEmploymentDocs(
        employee,
        company,
        includeExpired
      );

      let documents = employeeDocumentsData?.documents || [];
      if (documentType) {
        documents = documents.filter((doc) => {
          const fileName = doc.filename.toLowerCase();
          return fileName.startsWith(documentType.toLowerCase());
        });
      }

      const rows = getEmployeeDocumentsListData({ documents });
      setEmployeeDocuments((prev) => ({
        ...prev,
        isLoading: false,
        rows,
      }));
      dispatch(
        setIsActiveCompanyAssociation(!!activeCompanyAssociations?.[company])
      );
    } catch (error) {
      setEmployeeDocuments((prev) => ({ ...prev, isLoading: false }));
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleCompany = (e) => {
    dispatch(setCompany(e.target.value));
  };

  const handleIncludeExpired = (e) => {
    dispatch(setIncludeExpired(e.target.value));
  };

  const handleDocumentType = (e) => {
    dispatch(setDocumentType(e.target.value));
  };

  const handleActiveCompanyAssociations = () => {
    setActiveCompanyAssociationsDialog(true);
  };

  const handleAssociateDocument = async () => {
    try {
      setIsLoadingBackDrop(true);
      const documentTypes = await getDocumentTypes();
      setDocumentTypes(documentTypes?.documentTypes);
      setAssociateDocumentDialog(true);
      setIsLoadingBackDrop(false);
    } catch (error) {
      setIsLoadingBackDrop(false);
      setAssociateDocumentDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleDownloadEmployeeDocument = async (params) => {
    const { row } = params;
    const { filename } = row;
    try {
      setIsLoadingBackDrop(true);
      const downloadLink = await postDownloadEmploymentDocument(
        employee,
        company,
        filename
      );
      const response = await fetch(downloadLink?.link);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const snackBar = getSnackBar('File downloaded successfully');
      dispatch(displaySnackbar(snackBar));
      setIsLoadingBackDrop(false);
    } catch (error) {
      setIsLoadingBackDrop(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleDeleteEmployeeDocument = (params) => {
    const { row } = params;
    const { filename } = row;
    setDeleteDocument(filename);
    setOpenConfirmationDialog(true);
  };

  const handleDeleteEmployeeDocumentApi = async () => {
    try {
      setLoadingButton(true);
      await deleteEmploymentDocument(employee, company, deleteDocument);
      fetchEmployeeDocuments();
      setLoadingButton(false);
      const snackBar = getSnackBar('Succesfully document has been deleted');
      dispatch(displaySnackbar(snackBar));
      setOpenConfirmationDialog(false);
    } catch (error) {
      setLoadingButton(false);
      setOpenConfirmationDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const documentTypesOptions = documentTypes?.length
    ? documentTypes.map((item) => ({
        id: item?.type,
        value: item?.type?.charAt(0).toUpperCase() + item?.type.slice(1),
      }))
    : documentType
    ? [
        {
          id: documentType,
          value: documentType.charAt(0).toUpperCase() + documentType.slice(1),
        },
      ]
    : [];

  return (
    <>
      {showBackdropLoader(isLoadingBackDrop)}
      <HeadingMuiTypography variant="h6">
        Employee Documents
      </HeadingMuiTypography>
      <GridBaselineWrapper container spacing={2}>
        <Grid item xs={12} sm={2.5}>
          <FormInputDropdown
            name="company"
            control={control}
            options={companyOptions}
            label="Company"
            register={register('company', {
              onChange: (e) => {
                handleCompany(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <FormInputDropdown
            name="documentType"
            control={control}
            options={documentTypesOptions}
            label="Document Type"
            register={register('documentType', {
              onChange: (e) => handleDocumentType(e),
            })}
            filter
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <FormInputDropdown
            name="includeExpired"
            control={control}
            options={booleanOptions}
            label="Include Expired"
            register={register('includeExpired', {
              onChange: (e) => {
                handleIncludeExpired(e);
              },
            })}
          />
        </Grid>
        <Grid item xs={12} sm={2.25}>
          <Button variant="text" onClick={handleActiveCompanyAssociations}>
            Active Company Associations
          </Button>
        </Grid>
        <Grid item xs={12} sm={2.25}>
          <Button
            variant="text"
            disabled={!isActiveCompanyAssociation}
            onClick={handleAssociateDocument}
          >
            Associate Document
          </Button>
        </Grid>
      </GridBaselineWrapper>
      <Box mt={1}></Box>
      <DeltaDataGrid
        listData={employeeDocuments}
        columns={employeeDocumentsColumns(
          handleDownloadEmployeeDocument,
          handleDeleteEmployeeDocument
        )}
        setListData={pageSizeChange}
      />
      {!isActiveCompanyAssociation && (
        <>
          <Box mt={1}></Box>
          <GridWrapper container>
            <Typography variant="body2">
              The user is not currently associated with this company you may not
              add documents for this company.
            </Typography>
          </GridWrapper>
        </>
      )}
      <ActiveCompanyAssociationsDialog
        activeCompanyAssociationsDialog={activeCompanyAssociationsDialog}
        setActiveCompanyAssociationsDialog={setActiveCompanyAssociationsDialog}
        company={company}
      />
      <AssociateDocumentDialog
        associateDocumentDialog={associateDocumentDialog}
        setAssociateDocumentDialog={setAssociateDocumentDialog}
        fetchEmployeeDocuments={fetchEmployeeDocuments}
        setValue={setValue}
        documentTypes={documentTypes}
      />
      <ConfirmationDialog
        openConfirmationDialog={openConfirmationDialog}
        setOpenConfirmationDialog={setOpenConfirmationDialog}
        title="Are you sure you want to delete this document ?"
        handleClick={handleDeleteEmployeeDocumentApi}
        loadingButton={loadingButton}
      />
      <NotificationPopup />
    </>
  );
};

export default EmployeeDocumentsTab;
